/**
 * Shared text matching utilities used by both highlightOnCanvas (text-layer)
 * and matchCitationText (Textract LINE geometry fallback).
 */

/** Strip LLM-added list numbering from citation text (e.g. "1. foo 2. bar" → "foo bar"). */
/** Normalise text for matching: strip page markers, lowercase, collapse whitespace, remove punctuation. */
export function normalize(text: string): string {
  return text
    .replace(/##\s*Page\s+\d+(?:\s*\(OCR\))?/gi, ' ')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Longest Common Substring ratio.
 * Returns `lcsLength / min(a.length, b.length)`.
 * Inputs are capped to avoid O(n*m) blowup on very long strings.
 *
 * Page-text cap is sized to comfortably cover dense clinical pages (headers
 * + tables + multi-paragraph narrative often run past 4 000 normalised chars
 * before the cited region). At search ≤ 500 chars this is ~7.5 M ops, ~50 ms.
 */
export function lcsRatio(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a.length > 500) a = a.slice(0, 500);
  if (b.length > 15000) b = b.slice(0, 15000);

  const m = a.length;
  const n = b.length;
  let prev = new Array(n + 1).fill(0);
  let curr = new Array(n + 1).fill(0);
  let maxLen = 0;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        curr[j] = prev[j - 1] + 1;
        if (curr[j] > maxLen) maxLen = curr[j];
      } else {
        curr[j] = 0;
      }
    }
    [prev, curr] = [curr, prev];
    curr.fill(0);
  }

  return maxLen / Math.min(m, n);
}

/**
 * Longest Common Substring — returns both the ratio and the match position
 * within `b` (the page text). Used by highlightOnCanvas to locate the best
 * matching region when prefix-match fails.
 */
export function lcsMatch(a: string, b: string): { ratio: number; matchStart: number; matchEnd: number } {
  if (!a || !b) return { ratio: 0, matchStart: -1, matchEnd: -1 };
  if (a.length > 500) a = a.slice(0, 500);
  if (b.length > 15000) b = b.slice(0, 15000);

  const m = a.length;
  const n = b.length;
  let prev = new Array(n + 1).fill(0);
  let curr = new Array(n + 1).fill(0);
  let maxLen = 0;
  let matchEndJ = -1;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        curr[j] = prev[j - 1] + 1;
        if (curr[j] > maxLen) {
          maxLen = curr[j];
          matchEndJ = j;
        }
      } else {
        curr[j] = 0;
      }
    }
    [prev, curr] = [curr, prev];
    curr.fill(0);
  }

  const ratio = maxLen / Math.min(m, n);
  const matchStart = matchEndJ >= 0 ? matchEndJ - maxLen : -1;
  const matchEnd = matchEndJ >= 0 ? matchEndJ : -1;

  return { ratio, matchStart, matchEnd };
}
